"use client";

import { useState } from "react";
import PhosphorIcon, {
  type PhosphorIconName,
} from "@/shared/components/common/PhosphorIcon";
import LeafletMap from "@/shared/components/common/leafletMap/LeafletMapWrapper";
import type {
  HotelDetailV2Distance,
  HotelDetailV2Model,
} from "../model/hotelDetailV2.model";
import { EmptyContent } from "./AboutSection";

interface LocationSectionProps {
  hotel: HotelDetailV2Model;
}

type PoiCategory = "اطراف هتل" | "مکان‌های مهم شهر" | "حمل‌ونقل";

type LocationPoi = {
  id: string;
  name: string;
  dist: string;
  time: string;
  cat: PoiCategory;
  latitude: number;
  longitude: number;
  color: string;
  icon: PhosphorIconName;
  isSample?: boolean;
};

// TODO: Restore location filters after POI categories are connected to real data.
const CATEGORIES = ["همه"];
// const CATEGORIES = ["اطراف هتل", "مکان‌های مهم شهر", "حمل‌ونقل"];
const SAMPLE_POIS: LocationPoi[] = [
  {
    id: "sample-haram",
    name: "حرم امام رضا (ع)",
    dist: "۱.۲ کیلومتر",
    time: "۱۰ دقیقه پیاده",
    cat: "مکان‌های مهم شهر",
    latitude: 36.2879,
    longitude: 59.6157,
    color: "#16a34a",
    icon: "MapPin",
    isSample: true,
  },
  {
    id: "sample-airport",
    name: "فرودگاه مشهد",
    dist: "۱۸ کیلومتر",
    time: "۳۰ دقیقه با ماشین",
    cat: "حمل‌ونقل",
    latitude: 36.2352,
    longitude: 59.6408,
    color: "#2563eb",
    icon: "AirplaneTilt",
    isSample: true,
  },
  {
    id: "sample-bazar",
    name: "بازار رضا",
    dist: "۰.۸ کیلومتر",
    time: "۸ دقیقه پیاده",
    cat: "مکان‌های مهم شهر",
    latitude: 36.2863,
    longitude: 59.6132,
    color: "#d97706",
    icon: "ShoppingBag",
    isSample: true,
  },
  {
    id: "sample-park",
    name: "پارک ملت",
    dist: "۲.۱ کیلومتر",
    time: "۲۰ دقیقه پیاده",
    cat: "اطراف هتل",
    latitude: 36.3166,
    longitude: 59.5377,
    color: "#16a34a",
    icon: "Tree",
    isSample: true,
  },
];

export default function LocationSection({ hotel }: LocationSectionProps) {
  const [activePoi, setActivePoi] = useState<string | null>(null);
  const [category, setCategory] = useState("همه");
  const center = getMapCenter(hotel);
  const pois = createLocationPois(hotel.distances, center);
  const shownPois =
    category === "همه" ? pois : pois.filter((poi) => poi.cat === category);
  const activeFull = shownPois.find((poi) => poi.id === activePoi);
  const hasAnyLocationData = Boolean(hotel.coordinates || pois.length > 0);
  const displayAddress = cleanLocationText(hotel.address);

  return (
    <section
      id="location"
      className="scroll-mt-28 rounded-lg border border-[rgb(229,231,235)] bg-white p-5"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[#1a1a2e]">موقعیت مکانی هتل</h2>
          {displayAddress ? (
            <p className="mt-2 text-xs leading-6 text-[#6b7280]">{displayAddress}</p>
          ) : null}
        </div>
        {hotel.coordinates ? (
          <a
            href={createDirectionsHref(center.latitude, center.longitude)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[rgb(229,231,235)] px-3 py-2 text-xs font-bold text-[#6b7280] transition hover:border-primary hover:text-primary"
          >
            مسیریابی
            <PhosphorIcon name="NavigationArrow" size={13} />
          </a>
        ) : null}
      </div>

      {!hasAnyLocationData ? (
        <EmptyContent text="موقعیت و فاصله‌های این هتل ثبت نشده است." />
      ) : (
        <>
          <div className="relative h-[320px] w-full overflow-hidden rounded-2xl bg-[#e8edf0] md:h-[380px]">
            <LeafletMap
              latitude={center.latitude}
              longitude={center.longitude}
              className="h-[320px]! w-full rounded-2xl! md:h-[380px]!"
              markers={shownPois.map((poi) => ({
                id: poi.id,
                latitude: poi.latitude,
                longitude: poi.longitude,
                title: poi.name,
                subtitle: `${poi.dist} · ${poi.time}`,
                color: poi.color,
                active: activePoi === poi.id,
              }))}
              routeTo={
                activeFull
                  ? {
                      latitude: activeFull.latitude,
                      longitude: activeFull.longitude,
                    }
                  : null
              }
              onMarkerClick={(id) => setActivePoi(activePoi === id ? null : id)}
            />
            {activeFull ? (
              <div className="absolute bottom-3 right-3 flex items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-lg">
                <PhosphorIcon name="NavigationArrow" size={18} className="text-primary" />
                <div>
                  <p className="text-xs font-bold text-[#1a1a2e]">{activeFull.name}</p>
                  <p className="text-xs text-[#6b7280]">
                    {activeFull.dist} · {activeFull.time}
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setActivePoi(null);
                }}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition ${
                  category === item
                    ? "border-primary bg-primary/5 font-bold text-primary"
                    : "border-[rgb(229,231,235)] bg-white text-[#6b7280]"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="mt-4 grid gap-2 md:grid-cols-2">
            {shownPois.map((poi) => (
              <button
                key={poi.id}
                type="button"
                onClick={() => setActivePoi(activePoi === poi.id ? null : poi.id)}
                className={`flex items-center gap-3 rounded-xl border p-3 text-right transition-all hover:border-primary/20 ${
                  activePoi === poi.id
                    ? "border-primary bg-primary/5"
                    : "border-[rgb(229,231,235)] bg-white"
                }`}
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${poi.color}20`, color: poi.color }}
                >
                  <PhosphorIcon name={poi.icon} size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-[#1a1a2e]">
                    {poi.name}
                  </span>
                  <span className="mt-0.5 block text-xs text-[#6b7280]">
                    {poi.dist} · {poi.time}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function createLocationPois(
  distances: HotelDetailV2Distance[],
  center: { latitude: number; longitude: number },
): LocationPoi[] {
  if (!distances.length) return SAMPLE_POIS;

  const offsets = [
    [0.006, 0.004],
    [-0.008, 0.006],
    [0.004, -0.007],
    [-0.006, -0.004],
  ];

  return distances.slice(0, 4).map((distance, index) => {
    const cat = getCategory(distance);
    const [latOffset, lngOffset] = offsets[index % offsets.length];
    const visual = getPoiVisual(distance.title, cat);

    return {
      id: distance.id || `distance-${index}`,
      name: distance.title,
      dist: distance.distanceLabel,
      time: distance.travelTimeLabel || "زمان مسیر ثبت نشده",
      cat,
      latitude: center.latitude + latOffset,
      longitude: center.longitude + lngOffset,
      ...visual,
    };
  });
}

function getCategory(distance: HotelDetailV2Distance): PoiCategory {
  const text = `${distance.title} ${distance.mode || ""}`.toLowerCase();
  if (
    text.includes("فرودگاه") ||
    text.includes("راه") ||
    text.includes("ترمینال") ||
    text.includes("ایستگاه") ||
    text.includes("مترو") ||
    text.includes("airport") ||
    text.includes("station")
  ) {
    return "حمل‌ونقل";
  }
  if (
    text.includes("حرم") ||
    text.includes("بازار") ||
    text.includes("موزه") ||
    text.includes("پارک") ||
    text.includes("mall")
  ) {
    return "مکان‌های مهم شهر";
  }
  return "اطراف هتل";
}

function getPoiVisual(
  title: string,
  category: PoiCategory,
): { color: string; icon: PhosphorIconName } {
  const normalized = title.toLowerCase();
  if (normalized.includes("فرودگاه")) {
    return { color: "#2563eb", icon: "AirplaneTilt" };
  }
  if (normalized.includes("راه") || normalized.includes("ایستگاه")) {
    return { color: "#7c3aed", icon: "Train" };
  }
  if (normalized.includes("بازار") || normalized.includes("مال")) {
    return { color: "#d97706", icon: "ShoppingBag" };
  }
  if (normalized.includes("پارک")) {
    return { color: "#16a34a", icon: "Tree" };
  }
  if (category === "حمل‌ونقل") return { color: "#2563eb", icon: "Car" };
  if (category === "مکان‌های مهم شهر") return { color: "#16a34a", icon: "MapPin" };
  return { color: "var(--primary)", icon: "Buildings" };
}

function getMapCenter(hotel: HotelDetailV2Model) {
  return {
    latitude: hotel.coordinates?.latitude ?? 36.287,
    longitude: hotel.coordinates?.longitude ?? 59.615,
  };
}

function createDirectionsHref(
  latitude: number,
  longitude: number,
) {
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
}

function cleanLocationText(value?: string) {
  return value
    ?.replace(/\[[^\]]+\]\([^)]+\)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
